import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import {
    Grid,
    IconButton,
    Typography,
    Stack,
    CardHeader
} from '@mui/material';
import Loading from './Loading';
import Routes from '../routes';
import { CreateAlert, AlertTypes } from './Alert/AlertUtil';
import { DetailPanel, DetailPanelData } from "./Common/DetailPanel";
import {
    PrimaryButton,
    SecondaryButton,
    Error500,
    Table,
    Column,
    BreadCrumbs,
    Tabs,
    ActionBox,
    DataGrid,
    PageTitleWithToolTip,
    Error404,
} from './Common';
import { isDesktop, isMobile, trakrefFontFamily, readJsonSafe, getResponseErrorMessage } from '../utils';
import { Pill } from "./Common/Pill"
import FormatColorResetIcon from '@mui/icons-material/FormatColorReset';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DoDisturbAltOutlinedIcon from '@mui/icons-material/DoDisturbAltOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import ReOpenModal from "./WorkOrder/Components/ReOpenModal";
import CancelWorkOrderModal from "./WorkOrder/Components/CancelWorkOrderModal";
import CloseWorkOrderModal from "./WorkOrder/Components/CloseWorkOrderModal";
import ApproveWorkOrderModal from "./WorkOrder/Components/ApproveWorkOrderModal";
import AssignWorkOrderModal from "./WorkOrder/Components/AssignWorkOrderModal";
import Documents from "./Common/Documents";
import UserInfo from '../UserInfo';
import ProposalModal from './WorkOrder/Components/ProposalModal';
import UpdateProposalModal from './WorkOrder/Components/UpdateProposalModal';
import DeleteWorkOrderModal from './WorkOrder/Components/DeleteWorkOrderModal';
import { hotjar } from 'react-hotjar';
import { Translate, TranslateW } from '../Utils/Languages/Translate';
import { determineColorStatus } from "../color_utils";
import "./WorkOrderDetails.css";
import AirFilterWavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Discussion from './Inventory/Discussion';
import Enums from '../enums';
import useNavigate from '../hooks/useNavigate';
import useBreadCrumbs from '../hooks/useBreadCrumbs';
import { canCloseWorkOrder, canReopenWorkOrder } from './WorkOrder/utils';
import CustomTooltip from './Common/CustomTooltip';
import { text_commonCellProps, getLocalStorageKey } from './Common/DataGrid/utils'
import { StyledCellLink, Weight, Percentage } from '../components/Common/DataGrid/CellComponents'
import {
    getDateRangeFilterOperator,
    getRangeFilterOperator
} from '../components/Common/DataGrid/FilterComponents';
import { formatDate } from './AddWorkOrder/utils';
import { Box, Button } from '@mui/material';

const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});

const columns = [
    {
        field: 'idString',
        headerName: 'Service Event ID #',
        ...text_commonCellProps,
        renderCell: ({ row }) => {
            return <StyledCellLink
                path={`/ServiceEvent/${row.id}`}
                text={row.idString}
                from={"Work Order Details"}
                primaryLink
            />
        }
    },
    {
        field: 'assetName',
        headerName: 'Asset Name',
        ...text_commonCellProps,
        renderCell: ({ row: { assetId, assetName } }) => {
            // No asset link on mobile/tablet
            return <StyledCellLink
                path={`/Inventory/${assetId}`}
                text={assetName}
                from={"Work Order Details"}
                primaryLink
            />
        }
    },
        {
        field: 'underRepairEventId',
        headerName: 'Under Repair Service Event',
        ...text_commonCellProps,
        renderCell: ({ row: { underRepairEventId } }) => {
            return <StyledCellLink
                path={`/ServiceEvent/${underRepairEventId}`}
                text={underRepairEventId}
                from={"Work Order Details"}
                primaryLink
            />
        }
    },
    {
        field: 'systemStatus',
        headerName: 'Operational Status',
        ...text_commonCellProps
    },
    {
        field: 'type',
        headerName: 'Type',
        ...text_commonCellProps
    },
    {
        field: 'serviceDate',
        headerName: 'Service Date',
        filterOperators: dateRangeFilterOperator
    },
    {
        field: 'materialType',
        headerName: 'Material Type',
        ...text_commonCellProps,
        initiallyVisible: false
    },
    {
        field: 'netGasAdded',
        headerName: 'Net Gas Added',
        valueGetter: (value) => value,
        filterOperators: rangeFilterOperator,
        flex: 1,
        renderCell: ({ row: { netGasAdded } }) => <Weight value={netGasAdded} />
    },
    {
        field: 'chargeCapacityLBS',
        headerName: 'Charge Capacity',
        valueGetter: (value) => value,
        filterOperators: rangeFilterOperator,
        flex: 1,
        initiallyVisible: false,
        renderCell: ({ row: { chargeCapacityLBS } }) => <Weight value={chargeCapacityLBS} />
    },
    {
        field: 'leakRate',
        headerName: 'Event leak rate',
        valueGetter: (value) => value,
        filterOperators: rangeFilterOperator,
        flex: 1,
        renderCell: ({ row: { leakRate } }) => <Percentage value={leakRate} />
    },
    {
        field: 'statusName',
        headerName: 'Service event status',
        ...text_commonCellProps
    },
    {
        field: 'primaryTechnicianName',
        headerName: 'Primary Technician',
        ...text_commonCellProps,
    },
    
]

const WorkOrderDetails = (props) => {
    const {
        root,
        history: {
            location: {
                state: {
                    InitialActiveTab = "",
                    initialCommentId = ""
                } = {}
            } = {}
        } = {}
    } = props;

    const from = props.history.location && props.history.location.state && 'from' in props.history.location.state ? props.history.location.state.from : null;

    const [id, setId] = useState(null);
    const [dueDate, setDueDate] = useState(null);
    const [locationId, setLocationId] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [priorityId, setPriorityId] = useState(null);
    const [priorityError, setPriorityError] = useState(null);
    const [priorityDescription, setPriorityDescription] = useState(null);
    const [priorityDescriptionError, setPriorityDescriptionError] = useState(null);
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
    const [purchaseOrderNumberError, setPurchaseOrderNumberError] = useState(null);
    const [scheduledDate, setScheduledDate] = useState(null);
    const [serviceCompanyId, setServiceCompanyId] = useState(null);
    const [serviceCompanyError, setServiceCompanyError] = useState(null);
    const [type, setType] = useState(null);
    const [workOrderStatus, setWorkOrderStatus] = useState('Open');
    const [workOrderType, setWorkOrderType] = useState(null);

    const [lowDollarLimit, setLowDollarLimit] = useState(null);
    const [highDollarLimit, setHighDollarLimit] = useState(null);
    const [totalCost, setTotalCost] = useState(null);
    const [linkedSourceSystem, setLinkedSourceSystem] = useState(null);
    const [linkedSourceLink, setLinkedSourceLink] = useState(null);
    const [linkedSourceSystemHeadShot, setLinkedSourceSystemHeadShot] = useState(null);
    const [assignedLocation, setAssignedLocation] = useState(null);

    const [actionModal, setActionModal] = useState(null);

    const [typeError, setTypeError] = useState(null);
    const [workOrderNumber, setWorkOrderNumber] = useState('');
    const [workOrderNumberError, setWorkOrderNumberError] = useState(null);

    const [successfulPopup, setSuccessfulPopup] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errorLoading, setErrorLoading] = useState(null);
    const [error404Loading, setError404Loading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [initialData, setInitialData] = useState(null);

    const [dateScheduled, setDateScheduled] = useState(null);
    const [workScheduled, setWorkScheduled] = useState(null);
    const [createdBy, setCreatedBy] = useState(null);
    const [updatedBy, setUpdatedBy] = useState(null);
    const [updatedOn, setUpdatedOn] = useState(null);
    const [location, setLocation] = useState(null);
    const [priority, setPriority] = useState(null);
    const [primaryTechnician, setPrimaryTechnician] = useState(null);
    const [secondaryTechnician, setSecondaryTechnician] = useState(null);
    const [serviceCompany, setServiceCompany] = useState(null);
    const [statusReason, setStatusReason] = useState(null);
    const [createdOn, setCreatedOn] = useState(null);
    const [activeTab, setActiveTab] = useState(1);

    const [hasServiceEvent, setHasServiceEvent] = useState(null);
    const [hasOpenServiceEvent, setHasOpenServiceEvent] = useState(null);
    const [hasServiceEventWithAssetUnderRepair, setHasServiceEventWithAssetUnderRepair] = useState(null);
    const [workOrderAbleToClose, setWorkOrderAbleToClose] = useState(false);
    const [workOrderData, setWorkOrderData] = useState(null);

    const [modalPayload, setModalPayload] = useState({});
    const [proposalPaylaod, setProposalPayload] = useState({});
    const [updateData, setUpdateData] = useState(false);

    const [siteLeakHasSystems, setSiteLeakHasSystems] = useState(false);

    const history = useHistory();
    const Location = useLocation();
    const navigate = useNavigate({
        pageName: 'WorkOrderDetails'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'WorkOrderDetails', text: `Work Order #${props.match.params.id}`, link: history.location.pathname }
    })
    const renderWorkOrderCreationDetails = UserInfo.isSystemAdministrator() || UserInfo.isTenantAdministrator()
    const isAdmin = (UserInfo.isAdminRole() || UserInfo.isTechnicianManager());
    const renderActionBox = workOrderStatus === 'Open' && !UserInfo.isReadOnly();
    const canEdit =  assignedLocation && !UserInfo.isReadOnly() && (
        (UserInfo.isSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator() || UserInfo.isTechnicianManager() || UserInfo.isTechnician())) ||
        (UserInfo.isServiceProvider() && (UserInfo.isTenantAdministrator() || UserInfo.isTechnician())) ||
        (UserInfo.isSystemAdministrator())
    );

    const responsiveButtonStyle = {
        width: { xs: '100%', md: 'auto' },
        fontSize: { xs: '16px', lg: '14px' },
        padding: { xs: '12px 16px', lg: '6px 16px' },
        minHeight: { xs: '48px', lg: 'auto' }
    };

    const fetchData = async () => {
        //start temp code for removing service event column cache, columns changed
        const savedTableState = JSON.parse(localStorage.getItem(getLocalStorageKey(
            Routes.WorkOrder.ServiceEventTable(props.match.params.id)
        )));
        const tableCacheCleared = localStorage.getItem("workOrderServiceEventTableCacheCleared");

        if (savedTableState && !tableCacheCleared) {
            localStorage.removeItem(getLocalStorageKey(Routes.WorkOrder.ServiceEventTable(props.match.params.id)));
            localStorage.setItem("workOrderServiceEventTableCacheCleared", "true");
        }
        //end temp code for removing service event column cache, columns changed

        const response = await fetch(Routes.WorkOrder.Details(props.match.params.id));

        try {
            const data = await response.json();
            const workOrderDataClone = {
                ...data,
                status: data.workOrderStatus
            };

            if (data.status === 404) {
                setError404Loading(true);
                setLoading(false);
                return;
            }

            if (data.errors) {
                setErrorLoading(true);
                setLoading(false);
                return;
            }

            let ModalObj = {}
            ModalObj.ID = props.match.params.id
            ModalObj.LocationID = data.locationID
            ModalObj.ServicerID = data.serviceCompanyID

            let ProposalObj = {}
            ProposalObj.LaborCost = data.laborCost || null
            ProposalObj.OtherCost = data.otherCost || null
            ProposalObj.MaterialCost = data.materialCost || null
            ProposalObj.ProposedWork = data.proposedWork || null
            ProposalObj.Terms = data.terms || null
            ProposalObj.Approved = data.approved || null
            ProposalObj.ApprovalNotes = data.ApprovedNotes || null
            ProposalObj.Approvers = data.approvers || null
            ProposalObj.ApprovedBy = data.approvedBy || null
            ProposalObj.ApprovedDate = data.approvedDate || null

            checkSiteLeakHasSystems();
            setModalPayload(ModalObj);
            setProposalPayload(ProposalObj);
            setHasServiceEvent(data.hasServiceEvents);
            setHasOpenServiceEvent(data.hasOpenServiceEvents);
            setHasServiceEventWithAssetUnderRepair(data.hasServiceEventWithAssetUnderRepair);
            setWorkOrderAbleToClose(canCloseWorkOrder(workOrderDataClone));
            setWorkOrderData(workOrderDataClone)
            setDateScheduled(data.scheduledDate);
            setWorkScheduled(data.workScheduled);
            setCreatedBy(data.createdByUser);
            setUpdatedBy(data.updatedByUser);
            setWorkOrderType(data.workOrderType);
            setLocation(data.locationName);
            setPriority(data.priority);
            setPrimaryTechnician(data.primaryTechnician);
            setSecondaryTechnician(data.secondaryTechnician);
            setServiceCompany(data.serviceCompany);
            setServiceCompanyId(data.serviceCompanyID);
            setStatusReason(data.statusReason);

            var date = new Date(data.createdOn);
            setCreatedOn(date.toLocaleString());

            var updateDate = new Date(data.updatedOn);
            setUpdatedOn(updateDate.toLocaleString());
            setWorkOrderStatus(data.workOrderStatus);
            setWorkOrderNumber(data.workOrderNumber);
            setPurchaseOrderNumber(data.purchaseOrderNumber);
            setLowDollarLimit(data.lowDollarLimit);
            setHighDollarLimit(data.highDollarLimit);
            setTotalCost(data.totalCost);
            setLinkedSourceSystem(data.linkedSourceSystem);
            setLinkedSourceLink(data.linkedSourceLink);
            setLinkedSourceSystemHeadShot(data.linkedSourceSystemHeadShot);
            setAssignedLocation(data.assignedLocation);

            setLoading(false);

            hotjar.stateChange(document.URL + '#ServiceEvents');
        } catch (error) {
            setErrorLoading(true);
            setLoading(false);
        }
    };

    useEffect(() => {
        setId(props.match.params.id);

        if (InitialActiveTab) {
            setActiveTab(InitialActiveTab);
        }
        if (Location.state != undefined && Location.state.proposalOn == true) {
            setActionModal("Proposal")
        }

        const storedTab = window.location.href.split('#')[1];
        if (storedTab) {
            setActiveTab(parseInt(storedTab));
        }

        if (modalPayload != null && modalPayload.ID != null) {
            fetchData();
        }
    }, []);

    useEffect(() => {
        setId(props.match.params.id);
        setLoading(true);
        fetchData();
        setLoading(false);

    }, [updateData, props.match.params.id]);

    const setData = (data) => {
        setDueDate(data.dueDate ? data.dueDate : '');
        setLocationId(data.locationId);
        setPriorityId(data.priorityId);
        setPriorityDescription(data.priorityDescription ? data.priorityDescription : '');
        setPurchaseOrderNumber(data.purchaseOrderNumber ? data.purchaseOrderNumber : '');
        setScheduledDate(data.scheduledDate);
        setServiceCompanyId(data.serviceCompanyID);
        setType(data.typeId);
        setWorkOrderNumber(data.workOrderNumber ? data.workOrderNumber : '');
        setWorkScheduled(data.workScheduled ? data.workScheduled : '');
    };

    const validate = () => {
        let noErrorFound = true;
        if (!type) {
            setTypeError("This field is required");
            noErrorFound = false;
        } else {
            setTypeError(null);
        }
        if (!workOrderNumber) {
            setWorkOrderNumberError("This field is required");
            noErrorFound = false;
        } else {
            setWorkOrderNumberError(null);
        }
        if (!locationId) {
            setLocationError("This field is required");
            noErrorFound = false;
        } else {
            setLocationError(null);
        }
        if (!serviceCompanyId) {
            setServiceCompanyError("This field is required");
            noErrorFound = false;
        } else {
            setServiceCompanyError(null);
        }
        //if (type === '1' && !workScheduled) {
        //    setWorkScheduledError("This field is required");
        //    noErrorFound = false;
        //} else {
        //    setWorkScheduledError(null);
        //}
        if (type === '2' && !priorityId) {
            setPriorityError("This field is required");
            noErrorFound = false;
        } else {
            setPriorityError(null);
        }
        if (type === '2' && !priorityDescription) {
            setPriorityDescriptionError("This field is required");
            noErrorFound = false;
        } else {
            setPriorityDescriptionError(null);
        }
        return noErrorFound;
    };

    const cancel = () => {
        setEditing(false);
        setData(initialData);
        setPurchaseOrderNumber('');
    };

    const submit = async () => {
        if (validate()) {

            let input = {
                WorkOrderId: id,
                typeId: type,
                workOrderNumber: workOrderNumber,
                purchaseOrderNumber: purchaseOrderNumber,
                locationId: locationId,
                serviceCompanyId: serviceCompanyId,
                scheduledDate: scheduledDate,
                workScheduled: workScheduled,
                dueDate: dueDate,
                priorityId: priorityId,
                problemDescription: priorityDescription

            };
            const response = await fetch(Routes.WorkOrder.UpdateWorkOrder(), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(input)
            });

            if (response.status === 200) {
                CreateAlert(AlertTypes.Success, 'Work Order Successfully Updated');
            } else {
                // AB#17142: show why the update was refused (e.g. no permission) instead of a generic failure
                CreateAlert(AlertTypes.Error, getResponseErrorMessage(response, await readJsonSafe(response), 'There was an error updated the Work Order'));
            }
        } else {
            return;
        }
    };

    const reset = () => {
        setDueDate(null);
        setLocationId('');
        setPriorityId('');
        setPriorityDescription('');
        setPriorityDescriptionError(null);
        setPurchaseOrderNumber('');
        setPurchaseOrderNumberError(null);
        setScheduledDate(null);
        setServiceCompanyId('');
        setType('');
        setWorkOrderNumber('');
        setWorkOrderNumberError(null);
        setWorkScheduled('');
        setSuccessfulPopup(false);
    };

    const checkSiteLeakHasSystems = async () => {
        const response = await fetch(Routes.ServiceEvent.SiteLeakHasSystems(props.match.params.id), { // SiteAirFilterChangeOut use the same as SiteLeakHasSystems
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        });

        const result = await response.json();
        setSiteLeakHasSystems(result == "TRUE");
    };

    const SiteAir = async () => {
        const response = await fetch(Routes.ServiceEvent.SiteLeakHasSystems(id), {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        });

        const result = await response.json();

        if (result == "TRUE") {
            navigate.push(`/WorkOrder/${id}/SiteAir`)
        }
        else if (result == 'FALSE') {
            setSiteLeakHasSystems(false);
            toast("No system for a Site Air Filter Change Out ", { duration: 6000 });
        }
    };

    const OpenSiteLeakInspection = async () => {
        const response = await fetch(Routes.ServiceEvent.SiteLeakHasSystems(id), {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        });

        const result = await response.json();

        if (result == "TRUE") {
            navigate.push(`/WorkOrder/${id}/SiteLeakInspection`)
        }
        else if (result == 'FALSE') {
            setSiteLeakHasSystems(false);
            toast("No system for a Site Leak Inspection", { duration: 6000 });
            // Add custom error message
        }
    };

    const OpenSiteShutdown = async () => {
        const response = await fetch(Routes.ServiceEvent.SiteLeakHasSystems(id), {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        });

        const result = await response.json();

        if (result == "TRUE") {
            navigate.push(`/WorkOrder/${id}/SiteShutdown`)
        }
        else if (result == 'FALSE') {
            setSiteLeakHasSystems(false);
            toast("No systems for a Site Shutdown", { duration: 6000 });
        }
    };

    const renderCancelOption = () => {
        const isAdmin = (
            UserInfo.isSystemAdministrator()
            || UserInfo.isSuperTenantAdmin()
            || UserInfo.isTenantAdministrator()
        );

        return (isAdmin && UserInfo.isTechnicianManager()) || !hasServiceEvent;
    };

    const ReopenAndApproveButton = ({ renderApproveButton }) => {
        // below is how it is set up on V3, please don't change from native to normal
        //(!(props.model.IsSuperTenantAdmin || props.model.IsNativeTenantAdmin || props.model.IsSystemAdmin || props.model.IsNativeTechnicianManager))
        if (!canReopenWorkOrder(serviceCompanyId)) {
            return null;
        }
        const canApproveRole = UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator();
        if (renderApproveButton && !(!isDesktop && UserInfo.isTechnician())) {
            return (
                <>
                    <Grid item style={{ marginLeft: "auto" }}>
                        <SecondaryButton
                            buttonText={"Re-Open"}
                            onClick={() => setActionModal("Reopen")}
                            sx={responsiveButtonStyle}
                        />
                    </Grid>
                    <Grid item style={{ marginLeft: 5 }}>
                        {(
                            (UserInfo.isNativeSystemOwner() && canApproveRole) ||
                            UserInfo.isSystemAdministrator()) &&
                            <PrimaryButton
                                buttonText={"Approve"}
                                onClick={() => setActionModal("Approve")}
                                sx={responsiveButtonStyle}
                            />
                        }
                    </Grid>
                </>
            );
        }

        return (
                <PrimaryButton
                    buttonText={"Re-Open"}
                    onClick={() => setActionModal("Reopen")}
                    sx={responsiveButtonStyle}
                />
        );
    };

    const getLocalTime = (date) => {
        const momentDate = moment(date).local();
        const localDate = momentDate.format('MM/DD/YYYY, h:mm A');
        return localDate;
    };

    const renderDeleteOption = () => {
        const isEnabledRole = (
            UserInfo.isSystemAdministrator()
            || UserInfo.isNativeSuperTenantAdministrator()
            || UserInfo.isNativeTenantAdministrator()
            || UserInfo.isNativeTechnicianManager()
        );

        return isEnabledRole && !hasServiceEvent;
    };

    const EditButton = () => (
        <>
            <SecondaryButton
                buttonText={Translate("servicerInstancesMainTab.edit")}
                onClick={() => navigate.push(`/EditWorkOrder/${id}`)}
                sx={{ width: { xs: '100%', sm: '50%', md: 'auto' } }}
            />
        </>
    );

    const CreateServiceEventButton = () => (
        <Grid item style={{ ...(!isMobile && { marginLeft: canEdit ? 5 : 'auto' }) }}>
            <PrimaryButton
                fullWidth
                buttonText={Translate("createServiceEvent")}
                disabled={statusReason === "Proposal Required"}
                onClick={() => navigate.push(`/CreateServiceEvent/${id}`)}
                sx={responsiveButtonStyle}
            />
        </Grid>
    );

    const determineActionsItems = () => {
        let actionItems = [];

        if (siteLeakHasSystems) {
            actionItems.push(
                {
                    title: Translate("workOrderDetailsMenu.change-out-air-filter"),
                    Icon: AirFilterWavesOutlinedIcon,
                    onClick: SiteAir
                },
                {
                    title: Translate("workOrderDetailsMenu.add-site-leak-inspection"),
                    Icon: FormatColorResetIcon,
                    onClick: OpenSiteLeakInspection
                },
                {
                    title: "Site Shutdown",
                    Icon: PowerSettingsNewIcon,
                    onClick: OpenSiteShutdown
                }
            );
        }

        if (isAdmin) {
            actionItems.push({
                title: TranslateW("createUpdateProposal"),
                Icon: EditNoteIcon,
                onClick: () => setActionModal("UpdateProposal")
            });
        }

        if (UserInfo.isNativeAdminRole() || UserInfo.isNativeTechnicianManager()) {
            actionItems.push({
                title: Translate("workOrderDetailsMenu.assign-work-order"),
                Icon: EngineeringOutlinedIcon,
                onClick: () => setActionModal("Assign Work Order")
            });
        }

        if (workOrderAbleToClose) {
            actionItems.push({
                title: Translate("workOrderCloseView.close-work-order"),
                Icon: CloseOutlinedIcon,
                onClick: () => setActionModal("Close")
            });
        }

        if (renderCancelOption()) {
            actionItems.push({
                title: Translate('workOrderCancel.cancel-work-order'),
                Icon: DoDisturbAltOutlinedIcon,
                onClick: () => setActionModal("Cancel")
            });
        }

        if (renderDeleteOption()) {
            actionItems.push({
                title: Translate('workOrderDetailsMenu.delete-work-order'),
                Icon: DeleteOutlineOutlinedIcon,
                onClick: () => setActionModal("Delete")
            });
        }
        
        // Only showing Close Work Order & Site Leak Inspection on Mobile
        if (isMobile) {
            actionItems = [];
            if (workOrderAbleToClose) {
                actionItems.push({
                    title: Translate("workOrderCloseView.close-work-order"),
                    Icon: CloseOutlinedIcon,
                    onClick: () => setActionModal("Close")
                });
            }
            
            actionItems.push({
                title: Translate("workOrderDetailsMenu.add-site-leak-inspection"),
                Icon: FormatColorResetIcon,
                onClick: OpenSiteLeakInspection
            });

            actionItems.push({
                title: "Site Shutdown",
                Icon: PowerSettingsNewIcon,
                onClick: OpenSiteShutdown
            });
        }

        return actionItems;
    };

    const tabLabels = !isDesktop
        ? [TranslateW("serviceEvents")]
        : [
            TranslateW("serviceEvents"),
            Translate("workOrderDetailsLaborTab.labor-summary"),
            "Documents",
            Translate("workOrderDetailsMainTab.work-activity"),
            Translate("workOrderDetailsMainTab.discussion")
        ];

    if (loading) {
        return <Loading />;
    }

    if (error404Loading) {
        return <Error404 />;
    }

    if (errorLoading) {
        return <Error500 />;
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
                sx={{ display: { xs: 'none', sm: 'block' } }}
            />

            {actionModal === 'Reopen' && (
                <ReOpenModal
                    payload={modalPayload}
                    modalOpen={actionModal === 'Reopen'}
                    onClose={() => setActionModal(null)} refreshRequired={() => window.location.reload(false)}
                />
            )}

            {actionModal === 'Cancel' && (
                <CancelWorkOrderModal
                    payload={modalPayload}
                    modalOpen={actionModal === 'Cancel'}
                    onClose={() => setActionModal(null)} refreshRequired={() => setUpdateData(!updateData)}
                />
            )}

            {actionModal === 'Close' && (
                <CloseWorkOrderModal
                    payload={modalPayload}
                    modalOpen={actionModal === 'Close'}
                    onClose={() => setActionModal(null)}
                    refreshRequired={() => window.location.reload(false)}
                />
            )}

            {actionModal === 'Approve' && (
                <ApproveWorkOrderModal
                    id={id}
                    modalOpen={actionModal === 'Approve'}
                    onClose={() => setActionModal(null)}
                    refreshRequired={() => setUpdateData(!updateData)}
                />
            )}

            {actionModal === 'Assign Work Order' && (
                <AssignWorkOrderModal
                    payload={modalPayload}
                    hasServiceEvent={hasServiceEvent}
                    modalOpen={actionModal === 'Assign Work Order'}
                    onClose={() => setActionModal(null)}
                    refreshRequired={() => setUpdateData(!updateData)}
                />
            )}

            {(actionModal === 'Proposal' && !isAdmin) && (
                <ProposalModal
                    payload={modalPayload}
                    modalOpen={actionModal === 'Proposal'}
                    onClose={() => setActionModal(null)}
                    refreshRequired={() => setUpdateData(!updateData)}
                />
            )}

            {actionModal === 'UpdateProposal' && (
                <UpdateProposalModal
                    payload={modalPayload}
                    proposalData={proposalPaylaod}
                    modalOpen={actionModal === 'UpdateProposal'}
                    onClose={() => setActionModal(null)}
                    refreshRequired={() => setUpdateData(!updateData)}
                />
            )}

            {actionModal === 'Delete' && (
                <DeleteWorkOrderModal
                    payload={modalPayload}
                    proposalData={proposalPaylaod}
                    modalOpen={actionModal === 'Delete'}
                    onClose={() => setActionModal(null)}
                    refreshRequired={() => navigate.goBack("/WorkOrders", from)}
                />
            )}

            {/*State Button Privisions*/}
            {/* Approve Button = IsSystemAdmin || IsSuperTenantAdmin || IsNativeTenantAdmin || isNativeTechnicianManager*/}
            {/* Dont Show Cancel Option If: */}
            {/* ((!props.model.IsSystemAdmin && !props.model.IsSuperTenantAdmin && !props.model.IsTenantAdmin && !props.model.IsTechnicianManager && !props.model.CanSeeOtherServicers) ||*/}
            {/* props.WorkItemCount != 0 ||*/}
            {/* (props.model.WorkOrder.LocationInstanceID == 320 && (!props.model.IsSuperTenantAdmin && !props.model.IsSystemAdmin && !props.model.IsTenantAdmin && !props.model.CanSeeOtherServicers)))*/}
            {/*  Dont Show Delete Option If : */}
            {/*if (props.model.WorkOrder.LocationInstanceID == 523 && props.model.IsTechnicianManager)*/}
            {/* return null;*/}
            {/*if ((!props.model.IsSystemAdmin && !props.model.IsNativeSuperTenantAdmin && !props.model.IsNativeTenantAdmin && !props.model.IsNativeTechnicianManager) || props.WorkItemCount != 0 ||*/}
            {/*(props.model.WorkOrder.LocationInstanceID == 320 && (!props.model.IsNativeSuperTenantAdmin && !props.model.IsSystemAdmin && !props.model.IsNativeTenantAdmin)))*/}
            {/*// 'Update' has two modes */}

            <Grid
                container
                direction="row"
                alignItems="center"
                sx={{ pt: { xs: 2, sm: 0 }, px: { xs: 1, md: 0 } }}
            >
                <Grid item>
                    <IconButton
                        aria-label="close"
                        onClick={() => navigate.goBack("/WorkOrders", from)}
                        sx={{ pl: { xs: 1, sm: 0 } }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item>
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: { xs: '22px', md: '20px', lg: '22px' },
                            color: '#1A173B'
                        }}
                    >
                        {Translate('vitalStatsWeather.work-order')} {`#${id}`}
                    </Typography>
                </Grid>
                <Grid item sx={{ marginLeft: { xs: 0, sm: '0.5%' }, py: { xs: 3, sm: 0 } }}>
                    <Stack direction={'row'} flexWrap="nowrap" alignItems="center" sx={{ flexDirection: { xs: 'row-reverse', lg: 'row' } }}>
                        <Pill text={workOrderType} type='Normal' />
                        <Pill
                            text={workOrderStatus}
                            type={workOrderStatus}
                            bgColor={determineColorStatus(workOrderStatus)}
                        />
                        {
                            (linkedSourceSystemHeadShot && linkedSourceSystem) && (
                                <CustomTooltip title={`${linkedSourceSystem} CMMS Work Order`} placement="right">
                                    <img
                                        src={linkedSourceSystemHeadShot}
                                        style={{
                                            width: 'auto',
                                            height: '23px',
                                            marginLeft: '10px',
                                            cursor: linkedSourceLink ? "pointer" : "default"
                                        }}
                                        alt='cmms_icon'
                                        onClick={() => {
                                            if (linkedSourceLink) {
                                                window.open(linkedSourceLink, "_blank", "noopener,noreferrer");
                                            }
                                        }}
                                    />
                                </CustomTooltip>
                            )
                        }
                    </Stack>
                </Grid>
                {editing && (
                    <>
                        <Grid item style={{ marginLeft: 5 }}>
                            <SecondaryButton
                                buttonText='Cancel'
                                onClick={cancel}
                            />
                        </Grid>
                        <Grid item style={{ marginLeft: 5 }}>
                            <PrimaryButton
                                buttonText='Save Changes'
                                onClick={submit}
                            />
                        </Grid>
                    </>
                )}
                {!editing && (
                    <Grid sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        width: { xs: '100%', sm: '100%', md: 'auto' },
                        mx: { xs: 1 },
                        ml: { sm: 0, md: 'auto' },
                        mr: { xs: 1, sm: 0 }
                    }}>
                        {(workOrderStatus === 'Open' && !UserInfo.isReadOnly()) && (
                            <>
                                {canEdit && <EditButton />}
                                <CreateServiceEventButton />
                            </>
                        )}
                        {(workOrderStatus === 'Closed' || workOrderStatus === 'Cancelled') && (
                            <ReopenAndApproveButton renderApproveButton={false} />
                        )}
                        {workOrderStatus === 'Work Complete' && <ReopenAndApproveButton renderApproveButton />}
                    </Grid>
                )}
            </Grid>

            <Grid
                container
                spacing={2}
                mt={.75}
                sx={{ px: { xs: 1, sm: 0 } }}
            >
                <Grid item xs={12} md={9} lg={10}>
                    <DetailPanel expandable>
                        {!isDesktop && UserInfo.isTechnician() ? (
                            <>
                                <DetailPanelData title={TranslateW('workOrderRepeater.work-order-number')} data={workOrderNumber || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrderRepeater.repeaterItem-location')} data={location || "Unknown"} />
                                <DetailPanelData title={TranslateW("createWorkOrder.service-company")} data={serviceCompany || "Unknown"} />
                                <DetailPanelData title={TranslateW("workOrderDetailsMain.work-order-type")} data={workOrderType || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrderDetailsMain.status-reason')} data={statusReason || "Unknown"} />
                                <DetailPanelData title={'Request Details'} data={workScheduled || "Unknown"} />
                                <DetailPanelData title={TranslateW('serviceEventsView.date-scheduled')} data={formatDate(dateScheduled) || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrdersView.due-date')} data={formatDate(dueDate) || "Unknown"} />
                            </>
                        ) : (
                            <>
                                <DetailPanelData title={TranslateW('serviceEventsView.date-scheduled')} data={formatDate(dateScheduled) || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrderDetailsMain.scheduleMaintenanceDetailRow.work-scheduled-description')} data={workScheduled || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrderRepeater.work-order-number')} data={workOrderNumber || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrderRepeater.repeaterItem-location')} data={location} link={"/Location/"} linkId={modalPayload.LocationID} />
                                {workOrderType !== "Scheduled Maintenance" && (
                                    <DetailPanelData title={TranslateW("workOrdersView.priority")} data={priority || "Unknown"} />
                                )}
                                <DetailPanelData title="SCHEDULED TECHNICIANS"  data={ (primaryTechnician || secondaryTechnician) ? [primaryTechnician, secondaryTechnician] : "Unknown"} />
                                <DetailPanelData title={TranslateW("createWorkOrder.service-company")} data={serviceCompany || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrderDetailsMain.status-reason')} data={statusReason || "Unknown"} />
                                {renderWorkOrderCreationDetails && (
                                    <DetailPanelData title={TranslateW('workOrderDetailsMain.created-on')} data={getLocalTime(createdOn) || "Unknown"} />
                                )}
                                <DetailPanelData title={TranslateW('workOrderDetailsMain.purchase-order-number')} data={purchaseOrderNumber  || "Unknown"} />
                                <DetailPanelData title={TranslateW("highDollarLimit")} data={highDollarLimit || "Unknown"} />
                                <DetailPanelData title={TranslateW("lowDollarLimit")} data={lowDollarLimit || "Unknown"} />
                                <DetailPanelData title={TranslateW('workOrderLabor.material-total-cost')} data={totalCost || "Unknown"} />
                                {renderWorkOrderCreationDetails && (
                                    <>
                                        <DetailPanelData title={TranslateW("serviceEventView.created-by")} data={createdBy || "Unknown"} />
                                        <DetailPanelData title={TranslateW('serviceEventView.updated-by')} data={updatedBy || "Unknown"} />
                                        <DetailPanelData title={TranslateW("workOrderDetailsMain.updated-on")} data={getLocalTime(updatedOn) || "Unknown"} />
                                    </>
                                )}
                            </>
                        )}
                    </DetailPanel>
                </Grid>

                {renderActionBox && (
                    <Grid item xs={12} md={3} lg={2} sx={{ px: { xs: 1, md: 0 } }}>
                        <ActionBox
                            items={determineActionsItems()}
                        />
                    </Grid>
                )}
            </Grid>

            <div className="p-0 mt-3 col-md-12 f-jb">
                <Tabs
                    activeTab={activeTab}
                    onhandleChange={(event, newValue) => {
                        setActiveTab(newValue);
                        hotjar.stateChange(document.URL + '#' + tabLabels[newValue - 1]);
                    }}
                    labels={tabLabels}
                    tabElements={[
                        <>
                            <Grid>
                                <DataGrid
                                    url={Routes.WorkOrder.ServiceEventTable(props.match.params.id)}
                                    noResultText="Oops...It is empty here..."
                                    noResultText2="Create a new Service Event or change your search criteria."
                                    searchKeyword={history.location.state === undefined ? "" : history.location.state.searchKeyword}
                                    searchById={history.location.state === undefined ? "" : history.location.state.searchById}
                                    isOrCondition={history.location.state === undefined ? false : history.location.state.isOrCondition}
                                    columns={columns}
                                    hideExportButton={true}
                                    headerContent={
                                        <CardHeader
                                            title={
                                                <PageTitleWithToolTip
                                                    pageTitle={TranslateW("serviceActivityView.service-events")}
                                                    toolTipText={TranslateW("ServiceEventsDescription")}
                                                />
                                            }
                                            sx={{
                                                "& .MuiCardHeader-action": { display: "flex" },
                                                "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                                            }}
                                        />
                                    }
                                />
                            </Grid>
                        </>,
                        <>
                            <Grid
                                container
                                direction="row"
                                alignItems="center"
                                style={{ maxWidth: '55%', marginBottom: '2%' }}
                            >
                                <Grid item xs={12} lg={12} mb={1} pt={2}>
                                    <Table
                                        url={Routes.WorkOrder.LaborSummary(props.match.params.id)}
                                        tableHeader={Translate("workOrderDetailsLaborTab.labor-summary")}
                                        noResultText="Oops...It is empty here..."
                                        noResultText2="Please add Labour Hours when creating a Service Event."
                                    >
                                        <Column id='Date' title="DATE" property="date" type="dateTime" />
                                        <Column id='serviceEventsView.work-item' title="WORK ITEM" property="workItemName" />
                                        <Column id='workOrdersView.status' title="STATUS" property="workItemStatus" />
                                        <Column id='workItemDetailList.technician' title="TECHNICIAN" property="technician" />
                                        <Column id='workOrderLabor.labor' title="LABOR" property="totalHour" />
                                    </Table>
                                </Grid>
                            </Grid>
                        </>,
                        <>
                            <Grid
                                container
                                direction="row"
                                alignItems="center"
                                style={{ marginBottom: '2%' }}
                            >
                                <Grid item xs={12} lg={12} mb={1} pt={2}>
                                    <Documents
                                        showAddButton 
                                        id={props.match.params.id}
                                        fileType={16}
                                        fromPage={"WorkOrders"}
                                    />
                                </Grid>
                            </Grid>
                        </>,
                        <>
                            <Grid
                                container
                                direction="row"
                                alignItems="center"
                                style={{ maxWidth: '55%', marginBottom: '2%' }}
                            >
                                <Grid item xs={12} lg={12} mb={1} pt={2} align='center'>
                                    <Table
                                        url={Routes.WorkOrder.WorkActivity(props.match.params.id)}
                                        title={Translate("workOrderDetailsMainTab.work-activity")}
                                        noResultText="Oops...It is empty here..."
                                        noResultText2="Please add Work Activity to get started."
                                    >
                                        <Column id='Date' title="DATE" property="date" type="dateTime" />
                                        <Column id='assetNotesList.user' title="USER" property="name" />
                                        <Column id='Activity' title="ACTIVITY" property="activity" />
                                    </Table>
                                </Grid>
                            </Grid>
                        </>,
                        <>
                            <Grid
                                container
                            >
                                <Grid item xs={12} lg={12} mb={1} pt={2}>
                                    <Discussion itemID={id} type={Enums.CommentType.WorkOrder} initialCommentId={initialCommentId} />
                                </Grid>
                            </Grid>
                        </>


                    ]}
                    tabCustomStyle={{ ml: "25px" }}
                    tabPanelCustomStyle={{ ml: "8px" }}
                />
            </div>
        </>
    );
};

export default WorkOrderDetails;
